import { CountryCode } from "@commercekit/api";
import { useLocale } from "@commercekit/remix";
import { typedBoolean } from "@commercekit/shared";

export const useCheckoutCountry = (countryCode: string | undefined) => {
  const { locale, localeFullName } = useLocale();

  // Country names in the CountryCode enum are English (except NL/BE). Resolve
  // them per-domain via Intl.DisplayNames instead, e.g. "Germany" -> "Deutschland"
  // on the .de site. Falls back to the enum's English name if unsupported.
  let regionNames: Intl.DisplayNames | null = null;
  try {
    regionNames = new Intl.DisplayNames(localeFullName || "nl-nl", {
      type: "region",
    });
  } catch {
    regionNames = null;
  }

  const countryOptions = Object.entries(CountryCode).map(
    ([isoCode, englishName]) => ({
      name: regionNames?.of(isoCode) || englishName,
      value: isoCode,
    })
  );

  const getCountry = (countryCode: keyof typeof CountryCode) =>
    countryOptions?.find(
      (country) => country.value.toLowerCase() === countryCode.toLowerCase()
    );

  const loaceleDeliveryAddress: Record<
    string,
    { name: string; value: string }[]
  > = {
    nl: [getCountry("NL"), getCountry("BE")].filter(typedBoolean),
    NL: [getCountry("NL"), getCountry("BE")].filter(typedBoolean),
    fr: [getCountry("FR"), getCountry("BE")].filter(typedBoolean),
    FR: [getCountry("FR"), getCountry("BE")].filter(typedBoolean),
    de: [getCountry("DE"), getCountry("AT")].filter(typedBoolean),
    DE: [getCountry("DE"), getCountry("AT")].filter(typedBoolean),
    be: [getCountry("BE"), getCountry("FR")].filter(typedBoolean),
    BE: [getCountry("BE"), getCountry("FR")].filter(typedBoolean),
    dk: [getCountry("DK")].filter(typedBoolean),
    DK: [getCountry("DK")].filter(typedBoolean),
    da: [getCountry("DK")].filter(typedBoolean),
    DA: [getCountry("DK")].filter(typedBoolean),
    se: [getCountry("SE")].filter(typedBoolean),
    SE: [getCountry("SE")].filter(typedBoolean),
    sv: [getCountry("SE")].filter(typedBoolean),
    SV: [getCountry("SE")].filter(typedBoolean),
    pl: [getCountry("PL")].filter(typedBoolean),
    PL: [getCountry("PL")].filter(typedBoolean),
    it: [getCountry("IT")].filter(typedBoolean),
    IT: [getCountry("IT")].filter(typedBoolean),
    pt: [getCountry("PT")].filter(typedBoolean),
    PT: [getCountry("PT")].filter(typedBoolean),
    es: [getCountry("ES")].filter(typedBoolean),
    ES: [getCountry("ES")].filter(typedBoolean),
    gb: [getCountry("GB")].filter(typedBoolean),
    GB: [getCountry("GB")].filter(typedBoolean),
    ie: [getCountry("IE")].filter(typedBoolean),
    IE: [getCountry("IE")].filter(typedBoolean),
  };

  const checkoutCountries = loaceleDeliveryAddress[locale]?.length
    ? loaceleDeliveryAddress[locale]
    : countryOptions;

  const checkoutDefaultAddress = loaceleDeliveryAddress[locale]?.find(
    (country) => country.value.toLowerCase() === countryCode?.toLowerCase()
  );

  const disableCountryOption = !!countryCode && !!checkoutDefaultAddress;

  return {
    countryOptions,
    checkoutCountries,
    checkoutDefaultAddress,
    disableCountryOption,
    localeCountries: checkoutCountries,
    CountryCode,
  };
};
